import { createApi } from '@reduxjs/toolkit/query/react';
import { toGridQueryBody } from '@/components/CustomAgGrid/serverSide/gridRequest.ts';
import { appPortalBaseQuery } from '@/features/baseQuery.ts';
import { invalidateAnalytics } from '@/features/analytics/invalidate-analytics.ts';
import {
  toScopeBody,
  toScopeQueryString,
  withScopeQuery,
} from '@/features/analytics/analytics-scope.ts';
import type { AnalyticsScope } from '@/features/analytics/analytics-scope.def.ts';
import {
  BackendContactCommentDto,
  BackendContactPersonDto,
  BackendDashboardNavMonthsDto,
  BackendDashboardRecordsDto,
  BackendDashboardSummaryDto,
  BackendInteractionDetailDto,
  BackendInvestorInvestmentHistoryDto,
  BackendInvestorInteractionsListDto,
  BackendInvestorProfileDto,
  BackendInvestorsListDto,
  BackendIrTeamMemberDto,
  ContactCommentsResponse,
  ContactPerson,
  CreateContactCommentRequest,
  CreateContactCommentResponse,
  CreateContactPersonRequest,
  CreateContactPersonResponse,
  CreateInteractionRequest,
  CreateInteractionResponse,
  CreateInvestorRequest,
  CreateInvestorResponse,
  DeleteContactCommentRequest,
  DeleteContactCommentResponse,
  DeleteContactPersonRequest,
  DeleteContactPersonResponse,
  DeleteInteractionRequest,
  DeleteInteractionResponse,
  GetContactCommentsRequest,
  GetContactPersonsRequest,
  GetInvestorActivityHistoryRequest,
  GetInvestorInteractionFilterValuesRequest,
  GetInvestorInteractionsRequest,
  GetInvestorsDashboardFilterValuesRequest,
  GetInvestorsDashboardRecordsRequest,
  InteractionsResponse,
  InvestorActivityHistoryResponse,
  InvestorInteractionsResponse,
  InvestorListResponse,
  InvestorProfileResponse,
  IrTeamMembersResponse,
  UpdateContactCommentRequest,
  UpdateContactCommentResponse,
  UpdateContactPersonRequest,
  UpdateContactPersonResponse,
  UpdateInteractionRequest,
  UpdateInteractionResponse,
  UpdateInvestorProfileRequest,
  UpdateInvestorProfileResponse,
} from '@/features/investor-management/investor-management.def.ts';
import {
  InvestorBulkImportDryRunResultDto,
  InvestorBulkImportResultDto,
} from '@/features/investor-management/investor-bulk-upload.def.ts';
import {
  buildInteractionsScopeParams,
  mapContactComment,
  mapContactPerson,
  mapInteraction,
  mapInvestorInteractionRow,
  mapInvestorListItem,
  mapIrTeamMember,
  serializeInteractionRequest,
  transformInvestorProfile,
} from '@/features/investor-management/investor-management.mappers.ts';

interface InvestorBulkUploadRequest {
  file: File;
}

const createWorkbookFormData = (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return formData;
};

export const investorManagementApi = createApi({
  reducerPath: 'investorManagementApi',
  baseQuery: appPortalBaseQuery(),
  tagTypes: [
    'InvestorProfile',
    'InvestmentHistory',
    'Interactions',
    'ContactComments',
    'InvestorList',
    'DashboardSummary',
    'DashboardRecords',
    'DashboardNavMonths',
    'IrTeamMembers',
    'InvestorActivityHistory',
    'InvestorInteractions',
    'SnowflakeImportStatus',
  ],
  endpoints: (builder) => ({
    getInvestorList: builder.query<InvestorListResponse, void>({
      query: () => ({
        url: '/investors',
        method: 'GET',
      }),
      transformResponse: (response: BackendInvestorsListDto) =>
        response.items.map(mapInvestorListItem),
      providesTags: ['InvestorList'],
    }),
    getDashboardSummary: builder.query<BackendDashboardSummaryDto, AnalyticsScope | void>({
      query: (scope) => ({
        url: withScopeQuery('/investors/dashboard/summary', scope),
        method: 'GET',
      }),
      providesTags: ['DashboardSummary'],
    }),
    getDashboardRecords: builder.query<
      BackendDashboardRecordsDto,
      GetInvestorsDashboardRecordsRequest
    >({
      query: ({ page, pageSize, sort, filter, includeTotals, ...scope }) => ({
        url: '/investors/dashboard/records/query',
        method: 'POST',
        body: toGridQueryBody(
          { page, pageSize, sort, filter },
          // Only sent when asked, so requests without totals keep their existing body.
          { ...toScopeBody(scope), ...(includeTotals && { includeTotals: true }) },
        ),
      }),
      providesTags: ['DashboardRecords'],
    }),
    /** Distinct values of one free-valued grid column within the page scope (grid filters ignored). */
    getInvestorsDashboardFilterValues: builder.query<
      string[],
      GetInvestorsDashboardFilterValuesRequest
    >({
      query: ({ column, ...scope }) => {
        const qs = toScopeQueryString(scope, new URLSearchParams({ column }));

        return `/investors/dashboard/filter-values?${qs.toString()}`;
      },
      providesTags: ['DashboardRecords'],
    }),
    /** Unscoped on purpose, so a month already selected cannot vanish when funds change. */
    getDashboardNavMonths: builder.query<BackendDashboardNavMonthsDto, void>({
      query: () => ({
        url: '/investors/dashboard/nav-months',
        method: 'GET',
      }),
      providesTags: ['DashboardNavMonths'],
    }),
    downloadInvestorUploadWorkbook: builder.query<string, void>({
      query: () => ({
        url: '/investors/bulk/upload/workbook',
        method: 'GET',
        responseHandler: (response: Response) => response.blob(),
      }),
      transformResponse: (blob: Blob) => URL.createObjectURL(blob),
    }),
    dryRunInvestorUpload: builder.mutation<
      InvestorBulkImportDryRunResultDto,
      InvestorBulkUploadRequest
    >({
      query: ({ file }) => ({
        url: '/investors/bulk/upload/dry-run',
        method: 'POST',
        body: createWorkbookFormData(file),
      }),
    }),
    confirmInvestorUpload: builder.mutation<InvestorBulkImportResultDto, InvestorBulkUploadRequest>(
      {
        query: ({ file }) => ({
          url: '/investors/bulk/upload/confirm',
          method: 'POST',
          body: createWorkbookFormData(file),
        }),
        // Bare tags: the workbook spans an unknown set of investors, so every cached
        // per-investor entry of these types must be invalidated, not one specific id.
        // DashboardNavMonths included because a NAV upload is what introduces a new valuation month.
        invalidatesTags: [
          'InvestorList',
          'InvestorProfile',
          'DashboardSummary',
          'DashboardRecords',
          'DashboardNavMonths',
          'InvestorActivityHistory',
        ],
        // Investor status and geography feed the fund pages and NAV breakdown too.
        onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
          try {
            await queryFulfilled;
            invalidateAnalytics(dispatch);
          } catch {
            // The mutation error path shows the failure; nothing changed, so nothing to refresh.
          }
        },
      },
    ),
    createInvestor: builder.mutation<CreateInvestorResponse, CreateInvestorRequest>({
      query: (body) => ({
        url: '/investors',
        method: 'POST',
        body,
      }),
      transformResponse: (response: BackendInvestorProfileDto) =>
        transformInvestorProfile(response),
      invalidatesTags: ['InvestorList', 'DashboardRecords'],
    }),
    getInvestorProfile: builder.query<InvestorProfileResponse, string>({
      query: (investorId) => ({
        url: `/investors/${investorId}/profile`,
        method: 'GET',
      }),
      transformResponse: (response: BackendInvestorProfileDto) =>
        transformInvestorProfile(response),
      providesTags: (_result, _error, investorId) => [{ type: 'InvestorProfile', id: investorId }],
    }),
    updateInvestorProfile: builder.mutation<
      UpdateInvestorProfileResponse,
      UpdateInvestorProfileRequest
    >({
      query: ({ investorId, ...body }) => ({
        url: `/investors/${investorId}/profile`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { investorId }) => [
        { type: 'InvestorProfile', id: investorId },
        { type: 'InvestorList' },
        { type: 'DashboardSummary' },
        { type: 'DashboardRecords' },
        { type: 'InvestorActivityHistory', id: investorId },
      ],
    }),
    getInvestmentHistory: builder.query<BackendInvestorInvestmentHistoryDto, string>({
      query: (investorId) => ({
        url: `/investors/${investorId}/investments`,
        method: 'GET',
      }),
      providesTags: (_result, _error, investorId) => [
        { type: 'InvestmentHistory', id: investorId },
      ],
    }),
    getInteractions: builder.query<InteractionsResponse, string>({
      query: (investorId) => ({
        url: `/investors/${investorId}/interactions`,
        method: 'GET',
      }),
      transformResponse: (response: BackendInteractionDetailDto[]) => response.map(mapInteraction),
      providesTags: (_result, _error, investorId) => [{ type: 'Interactions', id: investorId }],
    }),
    getIrTeamMembers: builder.query<IrTeamMembersResponse, void>({
      query: () => ({
        url: '/ir-team-members',
        method: 'GET',
      }),
      transformResponse: (response: BackendIrTeamMemberDto[]) => response.map(mapIrTeamMember),
      providesTags: ['IrTeamMembers'],
    }),
    createInteraction: builder.mutation<CreateInteractionResponse, CreateInteractionRequest>({
      query: ({ investorId, ...body }) => ({
        url: `/investors/${investorId}/interactions`,
        method: 'POST',
        body: serializeInteractionRequest(body),
      }),
      transformResponse: (response: BackendInteractionDetailDto) => mapInteraction(response),
      invalidatesTags: (_result, _error, { investorId }) => [
        { type: 'Interactions', id: investorId },
        { type: 'InvestorActivityHistory', id: investorId },
        'InvestorInteractions',
      ],
    }),
    updateInteraction: builder.mutation<UpdateInteractionResponse, UpdateInteractionRequest>({
      query: ({ investorId, interactionId, ...body }) => ({
        url: `/investors/${investorId}/interactions/${interactionId}`,
        method: 'PUT',
        body: serializeInteractionRequest(body),
      }),
      invalidatesTags: (_result, _error, { investorId }) => [
        { type: 'Interactions', id: investorId },
        { type: 'InvestorActivityHistory', id: investorId },
        'InvestorInteractions',
      ],
    }),
    deleteInteraction: builder.mutation<DeleteInteractionResponse, DeleteInteractionRequest>({
      query: ({ investorId, interactionId }) => ({
        url: `/investors/${investorId}/interactions/${interactionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { investorId }) => [
        { type: 'Interactions', id: investorId },
        { type: 'InvestorActivityHistory', id: investorId },
        'InvestorInteractions',
      ],
    }),

    // Contact Person CRUD
    GetContactPersons: builder.query<ContactPerson[], GetContactPersonsRequest>({
      query: ({ investorId }) => ({
        url: `/investors/${investorId}/contacts`,
        method: 'GET',
      }),
      transformResponse: (response: BackendContactPersonDto[]) => response.map(mapContactPerson),
      providesTags: (_result, _error, { investorId }) => [
        { type: 'InvestorProfile', id: investorId },
      ],
    }),
    createContactPerson: builder.mutation<CreateContactPersonResponse, CreateContactPersonRequest>({
      query: ({ investorId, ...body }) => ({
        url: `/investors/${investorId}/contacts`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { investorId }) => [
        { type: 'InvestorProfile', id: investorId },
        { type: 'InvestorActivityHistory', id: investorId },
      ],
    }),
    updateContactPerson: builder.mutation<UpdateContactPersonResponse, UpdateContactPersonRequest>({
      query: ({ investorId, contactId, ...body }) => ({
        url: `/investors/${investorId}/contacts/${contactId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { investorId }) => [
        { type: 'InvestorProfile', id: investorId },
        { type: 'InvestorActivityHistory', id: investorId },
      ],
    }),
    deleteContactPerson: builder.mutation<DeleteContactPersonResponse, DeleteContactPersonRequest>({
      query: ({ investorId, contactId }) => ({
        url: `/investors/${investorId}/contacts/${contactId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { investorId }) => [
        { type: 'InvestorProfile', id: investorId },
        { type: 'InvestorActivityHistory', id: investorId },
      ],
    }),

    getInvestorActivityHistory: builder.query<
      InvestorActivityHistoryResponse,
      GetInvestorActivityHistoryRequest
    >({
      query: ({ investorId, ...params }) => ({
        url: `/investors/${investorId}/activity-history`,
        method: 'GET',
        params,
      }),
      providesTags: (_result, _error, { investorId }) => [
        { type: 'InvestorActivityHistory', id: investorId },
      ],
    }),

    getDecryptedInvestorActivityHistory: builder.query<
      InvestorActivityHistoryResponse,
      GetInvestorActivityHistoryRequest
    >({
      query: ({ investorId, ...params }) => ({
        url: `/investors/${investorId}/activity-history/decrypted`,
        method: 'GET',
        params,
      }),
      // Decrypted results are still never retained past unmount (keepUnusedDataFor: 0); the tag
      // only makes an open drawer refetch on mutation, matching the masked view's behaviour.
      keepUnusedDataFor: 0,
      providesTags: (_result, _error, { investorId }) => [
        { type: 'InvestorActivityHistory', id: investorId },
      ],
    }),

    // Contact Comments CRUD
    getContactComments: builder.query<ContactCommentsResponse, GetContactCommentsRequest>({
      query: ({ investorId, contactId }) => ({
        url: `/investors/${investorId}/contacts/${contactId}/comments`,
        method: 'GET',
      }),
      transformResponse: (response: BackendContactCommentDto[]) => response.map(mapContactComment),
      providesTags: (_result, _error, { contactId }) => [
        { type: 'ContactComments', id: contactId },
      ],
    }),
    createContactComment: builder.mutation<
      CreateContactCommentResponse,
      CreateContactCommentRequest
    >({
      query: ({ investorId, contactId, ...body }) => ({
        url: `/investors/${investorId}/contacts/${contactId}/comments`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { contactId }) => [
        { type: 'ContactComments', id: contactId },
      ],
    }),
    updateContactComment: builder.mutation<
      UpdateContactCommentResponse,
      UpdateContactCommentRequest
    >({
      query: ({ investorId, contactId, commentId, ...body }) => ({
        url: `/investors/${investorId}/contacts/${contactId}/comments/${commentId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { contactId }) => [
        { type: 'ContactComments', id: contactId },
      ],
    }),
    deleteContactComment: builder.mutation<
      DeleteContactCommentResponse,
      DeleteContactCommentRequest
    >({
      query: ({ investorId, contactId, commentId }) => ({
        url: `/investors/${investorId}/contacts/${contactId}/comments/${commentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { contactId }) => [
        { type: 'ContactComments', id: contactId },
      ],
    }),

    getInvestorInteractions: builder.query<
      InvestorInteractionsResponse,
      GetInvestorInteractionsRequest
    >({
      query: ({ page, pageSize, sort, filter, ...scope }) => ({
        url: '/investor-interactions/query',
        method: 'POST',
        body: toGridQueryBody({ page, pageSize, sort, filter }, scope),
      }),
      transformResponse: (
        response: BackendInvestorInteractionsListDto,
      ): InvestorInteractionsResponse => ({
        ...response,
        items: response.items.map(mapInvestorInteractionRow),
      }),
      providesTags: ['InvestorInteractions'],
    }),
    getInvestorInteractionFilterValues: builder.query<
      string[],
      GetInvestorInteractionFilterValuesRequest
    >({
      query: ({ column, ...scope }) => {
        const qs = buildInteractionsScopeParams(scope);
        qs.set('column', column);
        return `/investor-interactions/filter-values?${qs.toString()}`;
      },
      providesTags: ['InvestorInteractions'],
    }),
  }),
});

export const {
  useConfirmInvestorUploadMutation,
  useGetContactPersonsQuery,
  useGetDashboardSummaryQuery,
  useGetDashboardRecordsQuery,
  useGetInvestorsDashboardFilterValuesQuery,
  useGetDashboardNavMonthsQuery,
  useGetInvestorProfileQuery,
  useUpdateInvestorProfileMutation,
  useCreateInvestorMutation,
  useDryRunInvestorUploadMutation,
  useGetInvestmentHistoryQuery,
  useGetInteractionsQuery,
  useGetIrTeamMembersQuery,
  useLazyDownloadInvestorUploadWorkbookQuery,
  useCreateInteractionMutation,
  useUpdateInteractionMutation,
  useDeleteInteractionMutation,
  useCreateContactPersonMutation,
  useUpdateContactPersonMutation,
  useDeleteContactPersonMutation,
  useGetContactCommentsQuery,
  useCreateContactCommentMutation,
  useUpdateContactCommentMutation,
  useDeleteContactCommentMutation,
  useGetInvestorActivityHistoryQuery,
  useGetDecryptedInvestorActivityHistoryQuery,
  useGetInvestorInteractionsQuery,
} = investorManagementApi;
